import React, { useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { TESTNET_MAX_TICKET_GOAL_ENV } from "@config/config";
import { useTestnetProxyClient } from "@services/TestnetProxy/TestnetProxyContext";
import { useEventsModals } from "@app/panel/Events/contexts/EventsModals.context";

import Portal from "@components/overlay/Portal/Portal";
import Backdrop from "@components/ux/Backdrop/Backdrop";
import Modal from "@components/overlay/Modal/Modal";
import Stack from "@components/arrangement/Stack/Stack";
import Group from "@components/arrangement/Group/Group";
import Input from "@components/dataEntry/Input/Input";
import Button from "@components/dataEntry/Buttons/Button/Button";

import { generatedTicketsSchema } from "./generatedTickets.schema";
import "./TestnetGeneratedTickets.scss";

interface TestnetGeneratedTicketsForm {
  ticketsCount: number;
}

const TestnetGeneratedTickets = () => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { eventId } = useParams();

  const { mintTickets } = useTestnetProxyClient();
  const { closeModal } = useEventsModals();

  const { t } = useTranslation(["eventsModals", "common"]);
  const { t: errorsT } = useTranslation("formErrors");

  const validationSchema = generatedTicketsSchema(errorsT);

  const handleGeneratedTickets = ({ ticketsCount }: TestnetGeneratedTicketsForm) => {
    setSubmitting(true);

    mintTickets(eventId || "", ticketsCount)
      .then((res) => {
        if (res?.status === 204) {
          closeModal();
        }
      })
      .finally(() => setSubmitting(false));
  };

  const formik = useFormik<TestnetGeneratedTicketsForm>({
    initialValues: {
      ticketsCount: 0
    },
    validationSchema,
    onSubmit: handleGeneratedTickets
  });

  const { values, touched, errors, handleChange, handleBlur, handleSubmit } = formik;

  return (
    <Portal>
      <Backdrop open>
        <Modal onClose={closeModal}>
          <Stack className="testnet-generated-tickets-modal">
            <h2>{t("eventsModals:testnetGeneratedTickets.title")}</h2>
            <p className="p0 mt-4 mb-4">{t("eventsModals:testnetGeneratedTickets.description")}</p>

            <Input
              name="ticketsCount"
              type="number"
              label={t("eventsModals:testnetGeneratedTickets.inputs.ticketsCount.label")}
              placeholder={t("eventsModals:testnetGeneratedTickets.inputs.ticketsCount.placeholder")}
              minValue={0}
              maxValue={TESTNET_MAX_TICKET_GOAL_ENV}
              step={1}
              value={values.ticketsCount}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.ticketsCount && touched.ticketsCount ? errors.ticketsCount : ""}
              className="testnet-generated-tickets-modal-input"
              required
            />

            <Group colGap={20} className="mt-5">
              <Button variant="primary" onClick={handleSubmit} loading={submitting}>
                {t("common:generate")}
              </Button>
              <Button variant="secondary" onClick={closeModal}>
                {t("common:cancel")}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Backdrop>
    </Portal>
  );
};

export default TestnetGeneratedTickets;
